import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery } from '../../../hooks/use-app-query.hook';
import { ApiService } from '../../../services/index';
import { Safe } from '../../../types/safes.types';
import { useSafeConversionProgressStore } from '../convert-modal/conversion-progress.store';

const POLL_INTERVAL_MS = 5000;
// The `safe-conversion-completed` websocket event is the primary completion signal; polling is
// the fallback for when that event never reaches this client (socket on another backend replica,
// reconnect during a deploy). The timeout keeps a crashed background job from pinning the
// banner forever — after it fires the page just shows whatever state the server actually has.
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

export const SAFE_CONVERSION_RELATED_QUERY_KEYS = [
  QueryKey.GET_SAFES,
  QueryKey.GET_SAFE,
  QueryKey.GET_SAFE_CONVERSION,
  QueryKey.GET_SAFE_OVERALL_STATUS,
  QueryKey.GET_SAFE_RECAP,
] as const;

export const useSafeConversionProgressFallback = (companyId: string) => {
  const safeIds = useSafeConversionProgressStore((state) => state.safeIds);
  const startedAt = useSafeConversionProgressStore((state) => state.startedAt);
  const clearConverting = useSafeConversionProgressStore((state) => state.clearConverting);
  const queryClient = useQueryClient();

  const { data: polledSafes } = useAppQuery<Safe[]>({
    queryKey: [QueryKey.GET_SAFE_CONVERSION_PROGRESS, companyId, safeIds],
    queryFn: () =>
      Promise.all(
        safeIds.map((safeId) =>
          ApiService.get<Safe>({
            endpoint: BackendRoute.COMPANIES,
            routePath: [companyId, 'safes', safeId],
          }),
        ),
      ),
    refetchInterval: POLL_INTERVAL_MS,
    enabled: Boolean(companyId) && safeIds.length > 0,
    defaultErrorHandling: false,
  });

  useEffect(() => {
    if (safeIds.length === 0) return;

    const allConverted =
      polledSafes !== undefined &&
      polledSafes.length === safeIds.length &&
      polledSafes.every((safe) => safe.isConvertedToShares);
    const timedOut = startedAt !== null && Date.now() - startedAt > POLL_TIMEOUT_MS;
    if (!allConverted && !timedOut) return;

    clearConverting();
    for (const queryKey of SAFE_CONVERSION_RELATED_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    }
  }, [polledSafes, safeIds, startedAt, clearConverting, queryClient]);
};
